import React, { useState, useEffect, useMemo } from 'react';
import CommonModal from '../../Common/CommonModal';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import inventoryApi from '../../../services/inventoryApi';

const AddItemToRackModal = ({
  isOpen,
  onClose,
  warehouses = [],
  items = [],
  initialStore = '',
  initialRackId = '',
  initialLevelId = '',
  initialPositionId = '',
  onItemAllocated
}) => {
  const [store, setStore] = useState(initialStore || '');
  const [selectedRackId, setSelectedRackId] = useState(initialRackId || '');
  const [selectedLevelId, setSelectedLevelId] = useState(initialLevelId || '');
  const [selectedPositionId, setSelectedPositionId] = useState(initialPositionId || '');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');

  const [racks, setRacks] = useState([]);
  const [loadingRacks, setLoadingRacks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Stock details for selected item
  const [stockDetails, setStockDetails] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);

  // Filter items search inside dropdown
  const [itemSearchTerm, setItemSearchTerm] = useState('');

  // Sync initial props when modal opens, or reset to empty
  useEffect(() => {
    if (isOpen) {
      setStore(initialStore || '');
      setSelectedRackId(initialRackId || '');
      setSelectedLevelId(initialLevelId || '');
      setSelectedPositionId(initialPositionId || '');
      setSelectedItemId('');
      setQuantity('');
      setNotes('');
      setError('');
      setStockDetails(null);
    }
  }, [isOpen, initialStore, initialRackId, initialLevelId, initialPositionId]);

  // Load racks for selected store
  useEffect(() => {
    const fetchStoreRacks = async () => {
      if (!store) {
        setRacks([]);
        setSelectedRackId('');
        setSelectedLevelId('');
        setSelectedPositionId('');
        return;
      }
      try {
        setLoadingRacks(true);
        const res = await inventoryApi.getRacks({ store, limit: 100 });
        const list = res.data || [];
        setRacks(list);
      } catch (err) {
        console.error('Failed to load racks for store:', err);
        setRacks([]);
      } finally {
        setLoadingRacks(false);
      }
    };

    fetchStoreRacks();
  }, [store]);

  // Selected Rack Object
  const selectedRack = useMemo(() => {
    return racks.find(r => r._id === selectedRackId);
  }, [racks, selectedRackId]);

  // Levels for selected rack
  const availableLevels = useMemo(() => {
    return selectedRack?.levels || [];
  }, [selectedRack]);

  // Selected Level Object
  const selectedLevel = useMemo(() => {
    return availableLevels.find(l => l._id === selectedLevelId);
  }, [availableLevels, selectedLevelId]);

  // Positions for selected level
  const availablePositions = useMemo(() => {
    return selectedLevel?.positions || [];
  }, [selectedLevel]);

  // Fetch item's real-time stock details whenever Item or Store changes
  useEffect(() => {
    const fetchStockInfo = async () => {
      if (!selectedItemId) {
        setStockDetails(null);
        return;
      }
      try {
        setLoadingStock(true);
        const res = await inventoryApi.getItemLocationDetails(selectedItemId, {
          store: store || undefined
        });
        setStockDetails(res.data);
      } catch (err) {
        console.error('Failed to load item stock details:', err);
      } finally {
        setLoadingStock(false);
      }
    };

    fetchStockInfo();
  }, [selectedItemId, store]);

  // Filtered items list
  const filteredItemList = useMemo(() => {
    if (!itemSearchTerm.trim()) return items.slice(0, 50);
    const term = itemSearchTerm.toLowerCase();
    return items.filter(
      i =>
        i.name?.toLowerCase().includes(term) ||
        i.sku?.toLowerCase().includes(term) ||
        i.barcode?.toLowerCase().includes(term)
    ).slice(0, 50);
  }, [items, itemSearchTerm]);

  // Available unallocated stock
  const unallocatedStock = stockDetails?.stockSummary?.unallocatedStock ?? 0;
  const totalStoreStock = stockDetails?.stockSummary?.totalStock ?? 0;
  const totalAllocated = stockDetails?.stockSummary?.totalAllocated ?? 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!store) {
      setError('Please select Store / Warehouse');
      return;
    }
    if (!selectedRackId) {
      setError('Please select a Rack');
      return;
    }
    if (!selectedLevelId) {
      setError('Please select a Level');
      return;
    }
    if (!selectedPositionId) {
      setError('Please select a Position');
      return;
    }
    if (!selectedItemId) {
      setError('Please select an Item');
      return;
    }

    const numQty = parseFloat(quantity);
    if (isNaN(numQty) || numQty <= 0) {
      setError('Quantity must be greater than zero');
      return;
    }

    if (numQty > unallocatedStock) {
      setError(`Quantity (${numQty}) exceeds available unallocated stock (${unallocatedStock}) in this warehouse.`);
      return;
    }

    try {
      setSubmitting(true);
      await onItemAllocated({
        store,
        rackId: selectedRackId,
        levelId: selectedLevelId,
        positionId: selectedPositionId,
        itemId: selectedItemId,
        quantity: numQty,
        unit: stockDetails?.item?.unit || 'PCS',
        notes
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to allocate item');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Item to Storage Rack"
      size="lg"
      icon={
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Step 1: Location selection */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
            1. Target Storage Location
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <CustomSelect
                label="Store / Warehouse"
                required
                value={store}
                onChange={(e) => {
                  setStore(e.target.value);
                  setSelectedRackId('');
                  setSelectedLevelId('');
                  setSelectedPositionId('');
                }}
                placeholder="Select Store/Warehouse"
                options={warehouses.map(w => ({
                  value: w._id,
                  label: `${w.name} (${w.code || 'Store'})`
                }))}
              />
            </div>

            <div>
              <CustomSelect
                label="Rack"
                required
                disabled={!store || loadingRacks}
                value={selectedRackId}
                onChange={(e) => {
                  setSelectedRackId(e.target.value);
                  setSelectedLevelId('');
                  setSelectedPositionId('');
                }}
                placeholder={
                  !store
                    ? 'Select Store first'
                    : loadingRacks
                      ? 'Loading racks...'
                      : racks.length === 0
                        ? 'No racks in this warehouse (Create Rack first)'
                        : 'Select Rack'
                }
                options={racks.map(r => ({
                  value: r._id,
                  label: `Rack ${r.rackNumber} — ${r.name || (r.zone ? `Zone ${r.zone}` : 'General')}`
                }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <CustomSelect
                label="Level"
                required
                disabled={!selectedRackId || availableLevels.length === 0}
                value={selectedLevelId}
                onChange={(e) => {
                  setSelectedLevelId(e.target.value);
                  setSelectedPositionId('');
                }}
                placeholder={
                  !selectedRackId
                    ? 'Select Rack first'
                    : availableLevels.length === 0
                      ? 'No levels configured'
                      : 'Select Level'
                }
                options={availableLevels.map(l => ({
                  value: l._id,
                  label: `Level ${l.levelNumber} (${l.name || 'Tier'})`
                }))}
              />
            </div>

            <div>
              <CustomSelect
                label="Position"
                required
                disabled={!selectedLevelId || availablePositions.length === 0}
                value={selectedPositionId}
                onChange={(e) => setSelectedPositionId(e.target.value)}
                placeholder={
                  !selectedLevelId
                    ? 'Select Level first'
                    : availablePositions.length === 0
                      ? 'No positions configured'
                      : 'Select Position'
                }
                options={availablePositions.map(p => ({
                  value: p._id,
                  label: `Pos ${p.positionNumber} (${p.name || 'Available'})`
                }))}
              />
            </div>
          </div>
        </div>

        {/* Step 2: Item selection & stock verification */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-purple-600"></span>
            2. Select Item &amp; Check Available Stock
          </h4>

          {/* Quick item filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Search Item Master
            </label>
            <input
              type="text"
              placeholder="Type SKU or Item Name to filter list..."
              value={itemSearchTerm}
              onChange={(e) => setItemSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 mb-2"
            />
            <CustomSelect
              required
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              placeholder="Select an Item from catalog"
              options={filteredItemList.map(i => ({
                value: i._id,
                label: `[${i.sku}] ${i.name} — ${i.category?.name || 'Item'}`
              }))}
            />
          </div>

          {/* Real-time Stock Details Box */}
          {loadingStock && (
            <div className="p-3 bg-white border border-gray-200 rounded-lg text-center text-xs text-gray-500">
              Checking stock levels in ERP inventory...
            </div>
          )}

          {stockDetails && !loadingStock && (
            <div className="bg-white border border-indigo-100 rounded-xl p-3.5 shadow-sm space-y-2.5">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs font-bold text-gray-900">{stockDetails.item.name}</span>
                  <div className="text-[11px] text-gray-500">
                    SKU: <span className="font-semibold text-gray-800">{stockDetails.item.sku}</span> | Barcode:{' '}
                    <span className="font-semibold text-gray-800">{stockDetails.item.barcode}</span>
                  </div>
                </div>
                <span className="text-xs font-extrabold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                  {stockDetails.item.unit}
                </span>
              </div>

              {/* Stock KPI Pills */}
              <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-gray-100">
                <div className="p-2 rounded-lg bg-gray-50">
                  <span className="block text-[10px] text-gray-500 font-medium uppercase">Total In Store</span>
                  <span className="text-sm font-bold text-gray-900">{totalStoreStock}</span>
                </div>
                <div className="p-2 rounded-lg bg-amber-50">
                  <span className="block text-[10px] text-amber-700 font-medium uppercase">Already On Racks</span>
                  <span className="text-sm font-bold text-amber-800">{totalAllocated}</span>
                </div>
                <div className="p-2 rounded-lg bg-green-50 border border-green-200">
                  <span className="block text-[10px] text-green-700 font-extrabold uppercase">Unallocated Stock</span>
                  <span className="text-sm font-extrabold text-green-700">{unallocatedStock}</span>
                </div>
              </div>

              {unallocatedStock <= 0 && (
                <p className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200 font-medium">
                  ⚠️ No unallocated stock is currently available for this item in the selected store. Total stock ({totalStoreStock}) is already allocated to racks or zero.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Step 3: Quantity & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <TextInput
              label={`Quantity to Allocate ${stockDetails?.item?.unit ? `(${stockDetails.item.unit})` : ''}`}
              required
              type="number"
              min="0.01"
              step="any"
              max={unallocatedStock > 0 ? unallocatedStock : undefined}
              placeholder="e.g. 50"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              helperText={
                unallocatedStock > 0
                  ? `Max available to allocate: ${unallocatedStock}`
                  : 'Requires available unallocated stock'
              }
            />
          </div>

          <div>
            <TextInput
              label="Notes / Batch Reference"
              placeholder="e.g. Received under GRN-2026-001"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || (stockDetails && unallocatedStock <= 0)}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Allocating...</span>
              </>
            ) : (
              <span>Confirm Stock Allocation</span>
            )}
          </button>
        </div>
      </form>
    </CommonModal>
  );
};

export default AddItemToRackModal;
